import { Plus } from 'lucide-react';
import React from 'react';

import { CarrierNameSelect } from '@/components/carrier-directory/CarrierNameSelect';
import { DirectoryHandlesPreview } from '@/components/carrier-directory/DirectoryHandlesPreview';

import PromptBox from '../../PromptBox';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import SectionCollapse from '../../ui/SectionCollapse';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import DefendantCard from '../components/DefendantCard';
import StateSelect from '../components/StateSelect';
import { HEALTH_TYPES } from '../constants';
import { IntakeFormData } from '../form/types';

interface InsuranceSectionProps {
  form: IntakeFormData;
  onFieldChange: (
    section: keyof IntakeFormData,
    field: string,
    value: any,
  ) => void;
}

const InsuranceSection: React.FC<InsuranceSectionProps> = ({
  form,
  onFieldChange,
}) => {
  const handleInputChange = (fieldPath: string, value: string) => {
    onFieldChange('insurance', fieldPath, value);
  };

  const blankDefendant = {
    name: '',
    address: '',
    driversLicenseNumber: '',
    driversLicenseState: '',
    driversLicensePlate: '',
    makeModelYear: '',
    insuranceCompanyName: '',
    policyHolderName: '',
    policyNumber: '',
    claimNumber: '',
    carrierV2Id: null,
    carrierDivisionId: null,
  } as IntakeFormData['insurance']['defendants'][number];

  const handleDefendantFieldChange = (
    index: number,
    fieldPath: string,
    value: any,
  ) => {
    const updated = [...form.insurance.defendants];
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore dynamic path
    updated[index] = {
      ...updated[index],
      [fieldPath.split('.').pop()!]: value,
    };
    onFieldChange('insurance', 'defendants', updated);
  };

  const handleAddDefendant = () => {
    onFieldChange('insurance', 'defendants', [
      ...form.insurance.defendants,
      blankDefendant,
    ]);
  };

  const handleRemoveDefendant = (index: number) => {
    const updated = form.insurance.defendants.filter((_, i) => i !== index);
    onFieldChange('insurance', 'defendants', updated);
  };

  const [pipCarrierId, setPipCarrierId] = React.useState<string | null>(null);
  const [pipDivisionId, setPipDivisionId] = React.useState<string | null>(null);

  // Allow reading legacy camelCase keys in a typed way (backward-compat)
  type PipInsuranceWithLegacy = IntakeFormData['insurance']['pipInsurance'] & {
    carrierV2Id?: string | null;
    carrierDivisionId?: string | null;
  };
  const pipInsuranceWithLegacy = form.insurance
    .pipInsurance as PipInsuranceWithLegacy;

  // Initialize preview from previously selected directory IDs (persisted in form data)
  React.useEffect(() => {
    // Backward-compat: support legacy camelCase keys if present
    const storedCarrierId =
      pipInsuranceWithLegacy.carrier_v2_id ??
      pipInsuranceWithLegacy.carrierV2Id ??
      null;
    const storedDivisionId =
      pipInsuranceWithLegacy.carrier_division_id ??
      pipInsuranceWithLegacy.carrierDivisionId ??
      null;
    setPipCarrierId(storedCarrierId);
    setPipDivisionId(storedDivisionId);
  }, [
    pipInsuranceWithLegacy.carrier_v2_id,
    pipInsuranceWithLegacy.carrier_division_id,
    // Backward-compat deps for older saved intakes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    pipInsuranceWithLegacy.carrierV2Id,
    pipInsuranceWithLegacy.carrierDivisionId,
  ]);

  return (
    <div className="space-y-3">
      <PromptBox
        title="Suggested Questions"
        suggestions={[
          'Do you have the other driver&apos;s insurance information?',
          'What type of health insurance do you have?',
          'Have you reported this to your auto insurance?',
          'Do you have any other relevant insurance policies?',
        ]}
      />

      <div className="space-y-3">
        {/* PIP / First Party Insurance */}
        <SectionCollapse
          label="PIP / First Party Insurance"
          sectionId="pip-insurance"
          defaultOpen
          subtitle="Your personal injury protection or first-party coverage"
        >
          {/* Policy Info */}
          <div className="text-sm font-semibold text-gray-700 mt-2 mb-1">
            Policy Info
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="pipCompanyName" className="text-sm font-medium">
                Insurance Company Name
              </Label>
              <div className="flex items-center gap-2">
                <CarrierNameSelect
                  value={form.insurance.pipInsurance.companyName}
                  onChange={(name) =>
                    handleInputChange('pipInsurance.companyName', name)
                  }
                  onSelectDirectory={(carrierId, divisionId) => {
                    setPipCarrierId(carrierId);
                    setPipDivisionId(divisionId || null);
                    // Persist selection into intake form data (snake_case to match backend)
                    onFieldChange(
                      'insurance',
                      'pipInsurance.carrier_v2_id',
                      carrierId,
                    );
                    onFieldChange(
                      'insurance',
                      'pipInsurance.carrier_division_id',
                      divisionId || null,
                    );
                    // Backward-compat write (camelCase) for any code still reading legacy keys
                    onFieldChange(
                      'insurance',
                      'pipInsurance.carrierV2Id',
                      carrierId,
                    );
                    onFieldChange(
                      'insurance',
                      'pipInsurance.carrierDivisionId',
                      divisionId || null,
                    );
                  }}
                />
              </div>
              {pipCarrierId ? (
                <div className="mt-2">
                  <DirectoryHandlesPreview
                    carrierId={pipCarrierId}
                    divisionId={pipDivisionId}
                    title="Carrier Contacts (Directory)"
                    className="rounded border border-gray-200 p-2"
                  />
                </div>
              ) : null}
            </div>
            <div className="space-y-1">
              <Label
                htmlFor="pipPolicyHolderName"
                className="text-sm font-medium"
              >
                Policy Holder Name
              </Label>
              <Input
                id="pipPolicyHolderName"
                value={form.insurance.pipInsurance.policyHolderName}
                onChange={(e) =>
                  handleInputChange(
                    'pipInsurance.policyHolderName',
                    e.target.value,
                  )
                }
                placeholder="Name on the other party's policy"
                aria-label="PIP policy holder name"
                tabIndex={0}
                className="text-sm"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="pipPolicyNumber" className="text-sm font-medium">
                Policy Number
              </Label>
              <Input
                id="pipPolicyNumber"
                value={form.insurance.pipInsurance.policyNumber}
                onChange={(e) =>
                  handleInputChange('pipInsurance.policyNumber', e.target.value)
                }
                placeholder="Policy number"
                aria-label="PIP policy number"
                tabIndex={0}
                className="text-sm"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="pipClaimNumber" className="text-sm font-medium">
                Claim Number
              </Label>
              <Input
                id="pipClaimNumber"
                value={form.insurance.pipInsurance.claimNumber}
                onChange={(e) =>
                  handleInputChange('pipInsurance.claimNumber', e.target.value)
                }
                placeholder="Claim number if filed"
                aria-label="PIP claim number"
                tabIndex={0}
                className="text-sm"
              />
            </div>
          </div>

          {/* License Info */}
          <div className="text-sm font-semibold text-gray-700 mt-4 mb-1">
            License Info
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label
                htmlFor="pipDriversLicenseNumber"
                className="text-sm font-medium"
              >
                Driver&apos;s License Number
              </Label>
              <Input
                id="pipDriversLicenseNumber"
                value={form.personalInfo.driversLicenseNumber}
                onChange={(e) =>
                  onFieldChange(
                    'personalInfo',
                    'driversLicenseNumber',
                    e.target.value,
                  )
                }
                placeholder="Driver's License Number"
                aria-label="Driver's License Number"
                tabIndex={0}
                className="text-sm"
              />
            </div>
            <div className="space-y-1">
              <Label
                htmlFor="pipDriversLicenseState"
                className="text-sm font-medium"
              >
                Driver&apos;s License State
              </Label>
              <StateSelect
                id="pipDriversLicenseState"
                value={form.personalInfo.driversLicenseState}
                onChange={(value) =>
                  onFieldChange('personalInfo', 'driversLicenseState', value)
                }
              />
            </div>
          </div>

          {/* Vehicle Info */}
          <div className="text-sm font-semibold text-gray-700 mt-4 mb-1">
            Vehicle Info
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="pipMakeModelYear" className="text-sm font-medium">
                Make / Model / Year
              </Label>
              <Input
                id="pipMakeModelYear"
                value={form.insurance.pipInsurance.makeModelYear}
                onChange={(e) =>
                  handleInputChange(
                    'pipInsurance.makeModelYear',
                    e.target.value,
                  )
                }
                placeholder="e.g., 2018 Toyota Camry"
                aria-label="Vehicle make model year"
                tabIndex={0}
                className="text-sm"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="pipLicensePlate" className="text-sm font-medium">
                License Plate
              </Label>
              <Input
                id="pipLicensePlate"
                value={form.insurance.pipInsurance.licensePlate}
                onChange={(e) =>
                  handleInputChange('pipInsurance.licensePlate', e.target.value)
                }
                placeholder="License plate number"
                aria-label="Vehicle license plate"
                tabIndex={0}
                className="text-sm"
              />
            </div>
          </div>
        </SectionCollapse>

        {/* Health Insurance */}
        <SectionCollapse
          label="Health Insurance"
          sectionId="health-insurance"
          defaultOpen
          subtitle="Your health insurance coverage information"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="healthProvider" className="text-sm font-medium">
                Health Insurance Provider
              </Label>
              <Input
                id="healthProvider"
                value={form.insurance.healthProvider}
                onChange={(e) =>
                  handleInputChange('healthProvider', e.target.value)
                }
                placeholder="Health insurance company name"
                aria-label="Health insurance provider"
                tabIndex={0}
                className="text-sm"
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="healthType" className="text-sm font-medium">
                Insurance Type
              </Label>
              <Select
                value={form.insurance.healthType}
                onValueChange={(value) =>
                  handleInputChange('healthType', value)
                }
              >
                <SelectTrigger id="healthType" tabIndex={0} className="text-sm">
                  <SelectValue placeholder="Select insurance type" />
                </SelectTrigger>
                <SelectContent>
                  {HEALTH_TYPES.map((type) => (
                    <SelectItem key={type} value={type} className="text-sm">
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label htmlFor="healthMemberID" className="text-sm font-medium">
                Member ID
              </Label>
              <Input
                id="healthMemberID"
                value={form.insurance.healthMemberID}
                onChange={(e) =>
                  handleInputChange('healthMemberID', e.target.value)
                }
                placeholder="Your member ID number"
                aria-label="Health insurance member ID"
                tabIndex={0}
                className="text-sm"
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="healthGroupID" className="text-sm font-medium">
                Group ID
              </Label>
              <Input
                id="healthGroupID"
                value={form.insurance.healthGroupID}
                onChange={(e) =>
                  handleInputChange('healthGroupID', e.target.value)
                }
                placeholder="Group ID if applicable"
                aria-label="Health insurance group ID"
                tabIndex={0}
                className="text-sm"
              />
            </div>
          </div>
        </SectionCollapse>
        {/* Defendant(s) */}
        <SectionCollapse
          label=" BI / Third Party Insurance (Defendants) "
          sectionId="defendant-info"
          defaultOpen
          subtitle="Information about the other parties involved in the incident"
        >
          <div className="space-y-4">
            {form.insurance.defendants.map((defendant, idx) => (
              <DefendantCard
                key={idx}
                index={idx}
                defendant={defendant}
                onFieldChange={(field, val) =>
                  handleDefendantFieldChange(idx, field, val)
                }
                onRemove={() => handleRemoveDefendant(idx)}
              />
            ))}
            <div className="mt-1">
              <button
                type="button"
                onClick={handleAddDefendant}
                className="inline-flex items-center space-x-1 text-xs text-blue-600 hover:text-blue-700 hover:underline transition-colors"
                tabIndex={0}
                aria-label="Add another defendant"
              >
                <Plus size={12} />
                <span>Add another defendant</span>
              </button>
            </div>
          </div>
        </SectionCollapse>
      </div>
    </div>
  );
};

export default InsuranceSection;
